import { BRAND } from "@gridaction/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Coffee, Home, LogOut, Settings, ShieldCheck } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/overlay";
import { signOut as signOutEverywhere } from "@/lib/auth";
import { meQuery } from "@/lib/queries";

/** Pages that already ask a signed-out visitor to sign in, so the chip would only repeat it. */
const HAS_ITS_OWN_SIGN_IN = ["/", "/sign-in", "/sign-up", "/forgot-password", "/reset-password"];

export function initialsOf(name: string) {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?"
  );
}

/**
 * The corner of every page that says who you are signed in as, or offers to sign you in.
 * The avatar carries your initials and a green ring; open it for your name, email and sign out.
 */
export function AccountChip() {
  const { data: me } = useQuery({ ...meQuery, retry: false });
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const position = "fixed top-[calc(0.375rem+env(safe-area-inset-top))] right-3 z-50 print:hidden";

  if (me === undefined) return null;
  if (!me) {
    if (HAS_ITS_OWN_SIGN_IN.includes(pathname)) return null;
    return (
      <Link
        to="/sign-in"
        className={`${position} rounded-full border border-border bg-surface px-4 py-2 text-sm font-semibold shadow-sm hover:bg-surface-2`}
      >
        Sign in
      </Link>
    );
  }

  const signOut = async () => {
    await signOutEverywhere();
    queryClient.clear();
    navigate("/", { replace: true });
  };

  return (
    <div className={position}>
      <Menu>
        <MenuTrigger
          className="flex items-center gap-2 rounded-full bg-surface py-1 pr-1 pl-1 shadow-sm ring-2 ring-primary md:pr-3"
          aria-label={`Signed in as ${me.user.name}. Open account menu`}
        >
          <span className="grid size-9 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
            {initialsOf(me.user.name)}
          </span>
          <span className="hidden max-w-32 truncate text-sm font-semibold md:block">
            {me.user.name}
          </span>
        </MenuTrigger>
        <MenuContent>
          <MenuLabel>
            Signed in as {me.user.name}
            <span className="block font-normal text-muted">{me.user.email}</span>
          </MenuLabel>
          <MenuSeparator />
          <MenuItem onSelect={() => navigate("/app")}>
            <Home /> My grids
          </MenuItem>
          <MenuItem onSelect={() => navigate("/app/settings")}>
            <Settings /> Settings
          </MenuItem>
          {me.siteAdmin ? (
            <MenuItem onSelect={() => navigate("/app/admin")}>
              <ShieldCheck /> Admin
            </MenuItem>
          ) : null}
          {BRAND.tipUrl ? (
            <MenuItem
              onSelect={() => window.open(BRAND.tipUrl as string, "_blank", "noopener,noreferrer")}
            >
              <Coffee /> Leave a tip
            </MenuItem>
          ) : null}
          <MenuSeparator />
          <MenuItem onSelect={signOut}>
            <LogOut /> Sign out
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>
  );
}
